import React, { useState, useEffect } from "react";
import CustomDatePicker from "./CustomDatePicker";
import CustomInput from "./CustomInput";
import EmptyCalculator from "./EmptyCalculator";

const OptionsPriceCalculator = () => {
  // State for input values
  const [underlyingPrice, setUnderlyingPrice] = useState("");
  const [strikePrice, setStrikePrice] = useState("");
  const [volatility, setVolatility] = useState("");
  const [interestRate, setInterestRate] = useState("");
  const [dividendYield, setDividendYield] = useState("");
  const [expiryDate, setExpiryDate] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);

  // State for results
  const [results, setResults] = useState({
    callPrice: "-",
    putPrice: "-",
    callDelta: "-",
    putDelta: "-",
    gamma: "-",
    callTheta: "-",
    putTheta: "-",
    vega: "-",
  });

  // Check if all inputs are valid
  const isValidInput = () => {
    return (
      expiryDate != null &&
      underlyingPrice !== "" &&
      strikePrice !== "" &&
      volatility !== "" &&
      interestRate !== "" &&
      dividendYield !== ""
    );
  };

  // Format date to display in input field
  const formatDateForInput = (date) => {
    if (!date) return "";
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Handle date selection
  const handleDateSelect = (date) => {
    setExpiryDate(date);
    setShowDatePicker(false);
  };

  // Calculate option prices using Black-Scholes model
  const calculateOptions = () => {
    if (!isValidInput()) {
      setResults({
        callPrice: "-",
        putPrice: "-",
        callDelta: "-",
        putDelta: "-",
        gamma: "-",
        callTheta: "-",
        putTheta: "-",
        vega: "-",
      });
      return;
    }

    const S = parseFloat(underlyingPrice);
    const X = parseFloat(strikePrice);
    const sigma = parseFloat(volatility) / 100;
    const r = parseFloat(interestRate) / 100;
    const q = parseFloat(dividendYield) / 100;

    const currentDate = new Date();
    const daysToExpiry = Math.max(
      0,
      Math.min(
        365,
        Math.floor((expiryDate - currentDate) / (1000 * 60 * 60 * 24))
      )
    );
    const T = daysToExpiry / 365;

    // Black-Scholes formula calculations
    const d1 =
      (Math.log(S / X) + (r - q + (sigma * sigma) / 2) * T) /
      (sigma * Math.sqrt(T));
    const d2 = d1 - sigma * Math.sqrt(T);

    // Helper function for cumulative distribution function (CDF)
    const erf = (x) => {
      const a1 = 0.254829592;
      const a2 = -0.284496736;
      const a3 = 1.421413741;
      const a4 = -1.453152027;
      const a5 = 1.061405429;
      const p = 0.3275911;

      const sign = x < 0 ? -1 : 1;
      x = Math.abs(x);
      const t = 1.0 / (1.0 + p * x);
      const y =
        1 -
        ((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
      return sign * y;
    };

    // Normal cumulative distribution function
    const normalCDF = (x) => (1 + erf(x / Math.sqrt(2))) / 2;

    // Normal probability density function
    const normalPDF = (x) => Math.exp(-0.5 * x * x) / Math.sqrt(2 * Math.PI);

    // Calculate option prices and Greeks
    const callPrice =
      S * Math.exp(-q * T) * normalCDF(d1) -
      X * Math.exp(-r * T) * normalCDF(d2);
    const putPrice =
      X * Math.exp(-r * T) * normalCDF(-d2) -
      S * Math.exp(-q * T) * normalCDF(-d1);
    const callDelta = Math.exp(-q * T) * normalCDF(d1);
    const putDelta = -Math.exp(-q * T) * normalCDF(-d1);
    const gamma =
      (Math.exp(-q * T) * normalPDF(d1)) / (S * sigma * Math.sqrt(T));
    const callTheta =
      (-S * sigma * Math.exp(-q * T) * normalPDF(d1)) / (2 * Math.sqrt(T)) -
      r * X * Math.exp(-r * T) * normalCDF(d2) +
      q * S * Math.exp(-q * T) * normalCDF(d1);
    const putTheta =
      (-S * sigma * Math.exp(-q * T) * normalPDF(d1)) / (2 * Math.sqrt(T)) +
      r * X * Math.exp(-r * T) * normalCDF(-d2) -
      q * S * Math.exp(-q * T) * normalCDF(-d1);
    const vega = S * Math.exp(-q * T) * Math.sqrt(T) * normalPDF(d1);

    // Update results state with calculated values
    setResults({
      callPrice: callPrice.toFixed(4),
      putPrice: putPrice.toFixed(4),
      callDelta: callDelta.toFixed(4),
      putDelta: putDelta.toFixed(4),
      gamma: gamma.toFixed(4),
      callTheta: callTheta.toFixed(4),
      putTheta: putTheta.toFixed(4),
      vega: vega.toFixed(4),
    });
  };

  return (
    <div className="bg-body w-full h-full">
      <div className="flex gap-4 w-full">
        {/* Input Card */}
        <div className="bg-p400 rounded shadow-md w-1/2">
          <div className="p-4">
            <div className="mt-4">
              <CustomInput
                label="Spot Price"
                value={underlyingPrice}
                onChange={(e) => setUnderlyingPrice(e.target.value)}
                isNumberInput={true}
              />
              <CustomInput
                label="Strike"
                value={strikePrice}
                onChange={(e) => setStrikePrice(e.target.value)}
                isNumberInput={true}
              />
              <CustomInput
                label="Expiry Date"
                value={formatDateForInput(expiryDate)}
                isNumberInput={false}
                readOnly={true}
                onClick={() => setShowDatePicker(true)}
              />
              <CustomInput
                label="Volatility (%)"
                value={volatility}
                onChange={(e) => setVolatility(e.target.value)}
                isNumberInput={true}
              />
              <CustomInput
                label="Interest Rate (%)"
                value={interestRate}
                onChange={(e) => setInterestRate(e.target.value)}
                isNumberInput={true}
              />
              <CustomInput
                label="Dividend Yield (%)"
                value={dividendYield}
                onChange={(e) => setDividendYield(e.target.value)}
                isNumberInput={true}
              />
              <div className="my-4">
                <button
                  className="w-full bg-swipe-button text-p400 rounded py-2 px-4 text-sm font-medium"
                  onClick={calculateOptions}>
                  Calculate
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Results Card */}
        <div className="bg-p400 rounded shadow-md w-1/2">
          {isValidInput() && results.callPrice !== "-" ? (
            <div className="w-full overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-n200">
                    <th className="text-left p-4 text-xs font-medium text-n500">
                      Metric
                    </th>
                    <th className="text-left p-4 text-xs font-medium text-n500">
                      Call
                    </th>
                    <th className="text-left p-4 text-xs font-medium text-n500">
                      Put
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-n200">
                    <td className="p-4 text-sm font-medium text-p300">Price</td>
                    <td className="p-4 text-sm font-medium text-p300">
                      {results.callPrice}
                    </td>
                    <td className="p-4 text-sm font-medium text-p300">
                      {results.putPrice}
                    </td>
                  </tr>
                  <tr className="border-b border-n200">
                    <td className="p-4 text-sm font-medium text-p300">Delta</td>
                    <td className="p-4 text-sm font-medium text-p300">
                      {results.callDelta}
                    </td>
                    <td className="p-4 text-sm font-medium text-p300">
                      {results.putDelta}
                    </td>
                  </tr>
                  <tr className="border-b border-n200">
                    <td className="p-4 text-sm font-medium text-p300">Gamma</td>
                    <td className="p-4 text-sm font-medium text-p300">
                      {results.gamma}
                    </td>
                    <td className="p-4 text-sm font-medium text-p300">-</td>
                  </tr>
                  <tr className="border-b border-n200">
                    <td className="p-4 text-sm font-medium text-p300">Theta</td>
                    <td className="p-4 text-sm font-medium text-p300">
                      {results.callTheta}
                    </td>
                    <td className="p-4 text-sm font-medium text-p300">
                      {results.putTheta}
                    </td>
                  </tr>
                  <tr>
                    <td className="p-4 text-sm font-medium text-p300">Vega</td>
                    <td className="p-4 text-sm font-medium text-p300">
                      {results.vega}
                    </td>
                    <td className="p-4 text-sm font-medium text-p300">-</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyCalculator />
          )}
        </div>
      </div>

      {/* Date Picker Modal */}
      {showDatePicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-p400 rounded-lg shadow-xl max-w-md w-full">
            <CustomDatePicker
              onDateSelect={handleDateSelect}
              initialDate={expiryDate || new Date()}
              onCancel={() => setShowDatePicker(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default OptionsPriceCalculator;
